import * as React from 'react';
import { type MessageKey } from '@reos/i18n';
import { EmptyState } from '@redhand/ui';
import { PageHead } from '../components/page-head.js';
import { useT } from '../lib/i18n.js';
import { DomainApiError } from '../lib/api-error.js';
import { canonicalCzechNumber } from '../lib/czech-number.js';
import {
  bulkUpdateDeveloperProjectUnits,
  createDeveloperProject,
  importDeveloperProjectUnits,
  listDeveloperProjectUnits,
  listDeveloperProjects,
  pricelistCsvUrl,
  type DeveloperProjectListItemDto,
  type DeveloperUnitDto,
  type DeveloperUnitStatus,
  type UnitsResponseDto,
} from '../lib/developer-projects-api.js';

/**
 * `/projekty` (krok DEV1) — DEVELOPERSKE PROJEKTY: projekt -> tabulka
 * jednotek -> hromadna uprava cen a stavu -> cenik -> import z Excelu.
 *
 * Jednotka NENI paralelni svet: radek tabulky je radek ceniku, ktery
 * odkazuje na zakazku (`caseId`). Jednotka BEZ zakazky je v ceniku i na
 * webu projektu, ale na portal nejde — tabulka to u radku rika nahlas,
 * jinak by makler cekal inzerat, ktery nikdy nevznikne.
 *
 * ZMENA STAVU NA „rezervovana"/„prodana" oznaci i INZERAT (server vola
 * tutez cestu jako rucni zmena stavu nabidky na zakazce, krok LIST1) —
 * vysledek se ukazuje pod tabulkou, vcetne jednotek, u kterych se to
 * nepovedlo.
 */

const UNIT_STATUSES: readonly DeveloperUnitStatus[] = ['AVAILABLE', 'RESERVED', 'SOLD'];

const STATUS_LABEL: Readonly<Record<DeveloperUnitStatus, MessageKey>> = {
  AVAILABLE: 'webAdmin.developerProjects.status.available',
  RESERVED: 'webAdmin.developerProjects.status.reserved',
  SOLD: 'webAdmin.developerProjects.status.sold',
};

function money(amount: string | null, currency: string): string {
  if (amount === null) return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function area(value: string | null): string {
  if (value === null) return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 1 }).format(number)} m²`;
}

export function ProjektyPage(): React.JSX.Element {
  const t = useT();
  const [projects, setProjects] = React.useState<readonly DeveloperProjectListItemDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(null);
  const [units, setUnits] = React.useState<UnitsResponseDto | null>(null);
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<DeveloperUnitStatus | ''>('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [newName, setNewName] = React.useState('');
  const [importText, setImportText] = React.useState('');
  const [bulkStatus, setBulkStatus] = React.useState<DeveloperUnitStatus | ''>('');
  const [bulkPercent, setBulkPercent] = React.useState('');

  const reloadProjects = React.useCallback(async (): Promise<
    readonly DeveloperProjectListItemDto[]
  > => {
    const items = await listDeveloperProjects();
    setProjects(items);
    return items;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = await reloadProjects();
        if (!cancelled && items.length > 0) setSelectedProjectId(items[0]?.id ?? null);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof DomainApiError ? e.message : t('webAdmin.developerProjects.loadError'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadProjects]);

  const loadUnits = React.useCallback(
    async (projectId: string, status: DeveloperUnitStatus | ''): Promise<void> => {
      const result = await listDeveloperProjectUnits(projectId, status === '' ? {} : { status });
      setUnits(result);
    },
    [],
  );

  React.useEffect(() => {
    if (selectedProjectId === null) return;
    let cancelled = false;
    // Vyber jednotek patri k JEDNOMU projektu a JEDNOMU filtru — po zmene se
    // musi vynulovat, jinak by hromadna akce poslala id, ktera uz nevidim.
    setSelected([]);
    void (async () => {
      try {
        await loadUnits(selectedProjectId, statusFilter);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof DomainApiError ? e.message : t('webAdmin.developerProjects.loadError'),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, statusFilter, loadUnits]);

  const items: readonly DeveloperUnitDto[] = units?.items ?? [];
  const allSelected = items.length > 0 && selected.length === items.length;

  const toggleUnit = (unitId: string): void => {
    setSelected((current) =>
      current.includes(unitId) ? current.filter((id) => id !== unitId) : [...current, unitId],
    );
  };

  const onCreateProject = async (): Promise<void> => {
    setError(null);
    try {
      const created = await createDeveloperProject({ name: newName.trim() });
      setNewName('');
      await reloadProjects();
      setSelectedProjectId(created.id);
      setNotice(t('webAdmin.developerProjects.createdNotice', { name: created.name }));
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.developerProjects.saveError'));
    }
  };

  const onImport = async (): Promise<void> => {
    if (selectedProjectId === null || importText.trim() === '') return;
    setError(null);
    try {
      const result = await importDeveloperProjectUnits(selectedProjectId, importText);
      setImportText('');
      await loadUnits(selectedProjectId, statusFilter);
      await reloadProjects();
      setNotice(
        t('webAdmin.developerProjects.importNotice', {
          created: String(result.created),
          updated: String(result.updated),
          problems: String(result.problems.length),
        }),
      );
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.developerProjects.saveError'));
    }
  };

  const onBulk = async (): Promise<void> => {
    if (selectedProjectId === null || selected.length === 0) return;
    // Krok UXD2: tentyz parser jako ostatni ciselna pole adminu (SSOT),
    // ne vlastni `replace(',', '.')`, ktery neumi mezeru v tisicich.
    const percent = bulkPercent.trim() === '' ? undefined : Number(canonicalCzechNumber(bulkPercent));
    if (bulkStatus === '' && percent === undefined) return;
    setError(null);
    try {
      const result = await bulkUpdateDeveloperProjectUnits(selectedProjectId, {
        unitIds: selected,
        ...(bulkStatus === '' ? {} : { status: bulkStatus }),
        ...(percent === undefined || !Number.isFinite(percent) ? {} : { pricePercent: percent }),
        reason: t('webAdmin.developerProjects.bulkReason'),
      });
      setBulkStatus('');
      setBulkPercent('');
      setSelected([]);
      await loadUnits(selectedProjectId, statusFilter);
      await reloadProjects();
      setNotice(
        t('webAdmin.developerProjects.bulkNotice', {
          updated: String(result.updated),
          marked: String(result.listingMarked.length),
          skipped: String(result.listingSkipped.length),
        }),
      );
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.developerProjects.saveError'));
    }
  };

  return (
    <div className="p-6" data-testid="projekty-page">
      <PageHead title={t('webAdmin.developerProjects.pageTitle')} />
      <p className="mb-4 max-w-2xl text-sm text-gray-600">
        {t('webAdmin.developerProjects.perex')}
      </p>

      {error !== null && (
        <p role="alert" className="mb-3 text-red-600" data-testid="projekty-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="mb-3 text-sm text-gray-700" data-testid="projekty-notice">
          {notice}
        </p>
      )}

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          {t('webAdmin.developerProjects.newProjectLabel')}
          <input
            className="rh-input block"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="projekty-new-name"
          />
        </label>
        <button
          type="button"
          className="rh-btn"
          disabled={newName.trim() === ''}
          onClick={() => void onCreateProject()}
          data-testid="projekty-create"
        >
          {t('webAdmin.developerProjects.createProject')}
        </button>
      </div>

      {loading && <p role="status">{t('webAdmin.developerProjects.loading')}</p>}

      {!loading && projects.length === 0 ? (
        <EmptyState
          title={t('webAdmin.developerProjects.emptyTitle')}
          description={t('webAdmin.developerProjects.emptyDescription')}
        />
      ) : null}

      {projects.length > 0 ? (
        <ul className="mb-6 flex flex-wrap gap-3" data-testid="projekty-list">
          {projects.map((project) => (
            <li key={project.id}>
              <button
                type="button"
                className={project.id === selectedProjectId ? 'rh-btn' : 'rh-btn-ghost'}
                onClick={() => setSelectedProjectId(project.id)}
                data-testid={`projekty-item-${project.slug}`}
              >
                <span className="font-semibold">{project.name}</span>
                <span className="ml-2 text-xs text-gray-500">
                  {t('webAdmin.developerProjects.summaryShort', {
                    available: String(project.summary.available),
                    total: String(project.summary.unitCount),
                  })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {units !== null ? (
        <section data-testid="projekty-detail">
          <h2 className="mb-1 text-lg font-semibold">{units.project.name}</h2>
          <p className="mb-3 text-sm text-gray-600" data-testid="projekty-summary">
            {t('webAdmin.developerProjects.summaryLong', {
              total: String(units.summary.unitCount),
              available: String(units.summary.available),
              reserved: String(units.summary.reserved),
              sold: String(units.summary.sold),
              priceFrom: money(units.summary.priceFrom, units.summary.currency ?? 'CZK'),
            })}
          </p>

          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              {t('webAdmin.developerProjects.filterStatus')}
              <select
                className="rh-input block"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as DeveloperUnitStatus | '')}
                data-testid="projekty-filter-status"
              >
                <option value="">{t('webAdmin.developerProjects.filterAll')}</option>
                {UNIT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(STATUS_LABEL[status])}
                  </option>
                ))}
              </select>
            </label>
            <a
              className="rh-btn"
              href={pricelistCsvUrl(units.project.id)}
              target="_blank"
              rel="noopener"
              data-testid="projekty-pricelist-csv"
            >
              {t('webAdmin.developerProjects.downloadPricelist')}
            </a>
          </div>

          <div className="mb-4 rounded border border-gray-200 p-3" data-testid="projekty-bulk">
            <h3 className="mb-2 text-sm font-semibold">
              {t('webAdmin.developerProjects.bulkTitle', { count: String(selected.length) })}
            </h3>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                {t('webAdmin.developerProjects.bulkStatus')}
                <select
                  className="rh-input block"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value as DeveloperUnitStatus | '')}
                  data-testid="projekty-bulk-status"
                >
                  <option value="">{t('webAdmin.developerProjects.bulkKeep')}</option>
                  {UNIT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {t(STATUS_LABEL[status])}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                {t('webAdmin.developerProjects.bulkPercent')}
                <input
                  className="rh-input block"
                  inputMode="decimal"
                  value={bulkPercent}
                  onChange={(e) => setBulkPercent(e.target.value)}
                  data-testid="projekty-bulk-percent"
                />
              </label>
              <button
                type="button"
                className="rh-btn"
                disabled={selected.length === 0 || (bulkStatus === '' && bulkPercent.trim() === '')}
                onClick={() => void onBulk()}
                data-testid="projekty-bulk-apply"
              >
                {t('webAdmin.developerProjects.bulkApply')}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">{t('webAdmin.developerProjects.bulkHint')}</p>
          </div>

          {items.length === 0 ? (
            <EmptyState
              title={t('webAdmin.developerProjects.noUnitsTitle')}
              description={t('webAdmin.developerProjects.noUnitsDescription')}
            />
          ) : (
            <table className="w-full text-sm" data-testid="projekty-units">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pr-3 font-normal">
                    <input
                      type="checkbox"
                      aria-label={t('webAdmin.developerProjects.selectAll')}
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? [] : items.map((unit) => unit.id))}
                      data-testid="projekty-select-all"
                    />
                  </th>
                  <th className="pr-4 font-normal">{t('webAdmin.developerProjects.colLabel')}</th>
                  <th className="pr-4 font-normal">{t('webAdmin.developerProjects.colLayout')}</th>
                  <th className="pr-4 font-normal">{t('webAdmin.developerProjects.colArea')}</th>
                  <th className="pr-4 font-normal">{t('webAdmin.developerProjects.colFloor')}</th>
                  <th className="pr-4 font-normal">{t('webAdmin.developerProjects.colPrice')}</th>
                  <th className="pr-4 font-normal">{t('webAdmin.developerProjects.colStatus')}</th>
                  <th className="font-normal">{t('webAdmin.developerProjects.colListing')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((unit) => (
                  <tr key={unit.id} data-testid={`projekty-unit-${unit.label}`}>
                    <td className="pr-3">
                      <input
                        type="checkbox"
                        aria-label={t('webAdmin.developerProjects.selectUnit', {
                          label: unit.label,
                        })}
                        checked={selected.includes(unit.id)}
                        onChange={() => toggleUnit(unit.id)}
                        data-testid={`projekty-select-${unit.label}`}
                      />
                    </td>
                    <td className="pr-4 font-medium">{unit.label}</td>
                    <td className="pr-4">{unit.layout ?? '—'}</td>
                    <td className="pr-4">{area(unit.areaM2)}</td>
                    <td className="pr-4">{unit.floor === null ? '—' : String(unit.floor)}</td>
                    <td className="pr-4" data-testid={`projekty-price-${unit.label}`}>
                      {money(unit.price, unit.priceCurrency)}
                    </td>
                    <td className="pr-4" data-testid={`projekty-status-${unit.label}`}>
                      {t(STATUS_LABEL[unit.status])}
                    </td>
                    <td className="text-xs text-gray-500">
                      {unit.caseId === null
                        ? t('webAdmin.developerProjects.noCase')
                        : t('webAdmin.developerProjects.hasCase')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mt-6" data-testid="projekty-import">
            <h3 className="mb-1 text-sm font-semibold">
              {t('webAdmin.developerProjects.importTitle')}
            </h3>
            <p className="mb-2 max-w-2xl text-xs text-gray-500">
              {t('webAdmin.developerProjects.importHint')}
            </p>
            <textarea
              className="rh-input block w-full"
              rows={5}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              aria-label={t('webAdmin.developerProjects.importAriaLabel')}
              data-testid="projekty-import-text"
            />
            <button
              type="button"
              className="rh-btn mt-2"
              disabled={importText.trim() === ''}
              onClick={() => void onImport()}
              data-testid="projekty-import-apply"
            >
              {t('webAdmin.developerProjects.importApply')}
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
